import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/api";

// Thunk to add a medical file
export const add_medicalFile = createAsyncThunk(
  "medicalFile/add_medicalFile",
  async ({ userId, doctorId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await api.post("/add-medical-file", {
        userId,
        doctorId,
      });
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Unexpected error occurred"
      );
    }
  }
);

// Thunk to get medical file by doctor ID
export const get_medical_file_by_doctor_id = createAsyncThunk(
  "medicalFile/get_medical_file_by_doctor_id",
  async (doctorId, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.get(`/get-medical-file-by-doctor/${doctorId}`);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Thunk to get medical file by user ID
export const get_medical_file_by_user_id = createAsyncThunk(
  "medicalFile/get_medical_file_by_user_id",
  async (userId, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.get(`/get-medical-file-by-client/${userId}`);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Thunk to get admin medical files with pagination and search
export const get_admin_medical_file = createAsyncThunk(
  "medicalFile/get_admin_medical_file",
  async (
    { page, perPage, searchId },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const { data } = await api.get("/admin/get-admin-medical-file", {
        params: { page, perPage, searchId },
      });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const get_appts_of_medical_file = createAsyncThunk(
  "medicalFile/get_appts_of_medical_file",
  async (userId, { rejectWithValue, fulfillWithValue }) => {
    //console.log(userId);
    try {
      const response = await api.get(`/get-appts-of-medical-file/${userId}`);
      const data = response.data;
      //console.log(response);
      //console.log(data);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Unexpected error occurred"
      );
    }
  }
);

// Thunk to edit medical file
export const edit_medical_file = createAsyncThunk(
  "medicalFile/edit_medical_file",
  async (
    { medicalFileId, fileData, documents },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const { data } = await api.put(`/edit-medical-file/${medicalFileId}`, {
        fileData,
      });

      if (documents.length > 0) {
        const formData = new FormData();
        documents.forEach((doc) => formData.append("documents", doc.file));

        await api.put(`/upload-medical-documents/${medicalFileId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Thunk to edit medical status
export const edit_medical_status = createAsyncThunk(
  "medicalFile/edit_medical_status",
  async (
    { medicalFileId, medStatus },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const { data } = await api.post(`/edit-medical-status/${medicalFileId}`, {
        medStatus,
      });
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Thunk to delete a medical file
export const delete_medical_file = createAsyncThunk(
  "medicalFile/delete_medical_file",
  async (medicalFileId, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.delete(
        `/delete-medical-file/${medicalFileId}`
      );

      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const medicalFileReducer = createSlice({
  name: "medicalfile",
  initialState: {
    successMessage: "",
    errorMessage: "",
    loader: false,
    doctorMedicalFiles: [],
    clientMedicalFiles: [],
    adminMedicalFiles: [],
    adminTotalMedicalFiles: 0,
    allMedicalFiles: [],
    medStatus: "",
  },

  reducers: {
    messageClear: (state) => {
      state.errorMessage = "";
      state.successMessage = "";
    },
  },
  extraReducers: {
    [add_medicalFile.pending]: (state) => {
      state.loader = true;
      state.errorMessage = "";
      state.successMessage = "";
    },
    [add_medicalFile.fulfilled]: (state, { payload }) => {
      state.loader = false;
      state.successMessage = payload.message;
    },
    [add_medicalFile.rejected]: (state, { payload }) => {
      state.loader = false;
      state.errorMessage = payload.message || "Failed to book appointment.";
    },
    [get_medical_file_by_doctor_id.fulfilled]: (state, { payload }) => {
      state.doctorMedicalFiles = payload.doctorMedicalFiles;
    },
    [get_medical_file_by_user_id.fulfilled]: (state, { payload }) => {
      state.clientMedicalFiles = payload.clientMedicalFiles;
    },
    [get_admin_medical_file.fulfilled]: (state, { payload }) => {
      state.adminMedicalFiles = payload.adminMedicalFiles;
      state.adminTotalMedicalFiles = payload.adminTotalMedicalFiles;
    },
    [get_appts_of_medical_file.fulfilled]: (state, { payload }) => {
      state.allMedicalFiles = payload.allMedicalFiles;
      state.message = payload.message;
    },
    [edit_medical_file.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
    },
    [edit_medical_status.pending]: (state, { payload }) => {
      state.loader = true;
    },
    [edit_medical_status.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.loader = false;
      state.medStatus = payload.medStatus;
    },
    [edit_medical_status.rejected]: (state, { payload }) => {
      state.loader = false;
      state.errorMessage = payload.message;
    },
    [delete_medical_file.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
    },
  },
});

export const { messageClear } = medicalFileReducer.actions;
export default medicalFileReducer.reducer;
